import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  Link,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { HeaderOperationContent, HeaderSubjectContent } from "../types";
import { hasValue } from "../../../lib/util/common-util";
import { CMDatetimeText } from "../../text";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Link as RouterLink } from "react-router-dom";

export type CMCardEmailLogProps = {
  /**
   * オペレーション表示用データ
   */
  headerOperationItem: HeaderOperationContent[];

  date: Date;

  /**
   * 関連情報の表示
   */
  headerSubjectItem: HeaderSubjectContent[];

  /**
   * メールタイトル
   */
  title?: string;

  /**
   * メール本文
   */
  mainText?: string;

  /**
   * 開閉ボタンの開くときのラベル
   */
  labelButtonLabelOpen?: string;

  /**
   * 開閉ボタンの閉じるときのラベル
   */
  labelButtonLabelClose?: string;

  /**
   * 呼び出し元にボタンイベントを通知する
   */
  onOpenArea?: (isOpen: boolean) => void;
} & ComponentStyleProps;

/**
 * メールログカードコンポーネント
 */
export const CMCardEmailLog = ({
  headerOperationItem,
  date,
  headerSubjectItem,
  title,
  mainText,
  labelButtonLabelOpen,
  labelButtonLabelClose,
  onOpenArea,

  sx,
  ...rest
}: CMCardEmailLogProps) => {
  const styles = useMultiStyleConfig("CMCardEmailLogStyles");

  /**
   * 開閉状態
   * trueの場合: 開いている状態
   */
  const [isOpen, setStatus] = useState<boolean>(false);

  /**
   * コメントエリアの開閉スタイル
   */
  const appliedCommentStyle = useMemo(
    () => (isOpen ? styles.commentBoxOpen : styles.commentBoxClose),
    [isOpen, styles],
  );

  /**
   * 開閉イベントの通知
   */
  const handleToggle = useCallback(() => {
    const toggledValue = !isOpen;
    setStatus(toggledValue);
    onOpenArea?.(toggledValue);
  }, [isOpen, onOpenArea]);

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      <Box sx={styles.mainBlock}>
        {/*ヘッダエリア*/}
        <Box sx={styles.headerArea}>
          <Box sx={styles.operationAndDateArea}>
            {headerOperationItem.map((content, index) => (
              // オペレーション情報を出力するエリア
              <Box key={index} sx={styles.operationArea}>
                <Text sx={styles.operationLabel}>{content.label}</Text>
                <Text sx={styles.operationSeparator}>
                  {content.separator ?? ":"}
                </Text>
                <Text sx={styles.operationValue}>{content.value ?? "-"}</Text>
              </Box>
            ))}
            <CMDatetimeText value={date} sx={styles.date} />
          </Box>
          <Box sx={styles.subjectParentArea}>
            {headerSubjectItem.map((content, index) => (
              // 関連情報を出力するエリア
              <Box key={index} sx={styles.subjectChildArea}>
                <Text sx={styles.subjectLabel}>{content.label}</Text>
                <Text sx={styles.subjectSeparator}>
                  {content.separator ?? ":"}
                </Text>

                {/*urlが設定されている場合はリンク形式で表示*/}
                {hasValue(content.url) ? (
                  <Link
                    as={RouterLink}
                    to={content.url}
                    isExternal
                    sx={styles.subjectUrl}
                  >
                    {content.value ?? "-"}
                  </Link>
                ) : (
                  <Text sx={styles.subjectValue}>{content.value ?? "-"}</Text>
                )}
              </Box>
            ))}
          </Box>
          {/*メールタイトルを表示するエリア*/}
          <Box sx={styles.titleArea}>
            <Text sx={styles.title}>{title}</Text>
          </Box>
        </Box>
        {/*メール内容を表示するエリア*/}
        <Box sx={styles.mailTextArea}>
          {/*メール本文*/}
          <Box sx={appliedCommentStyle}>
            <Text sx={styles.comment}>{mainText}</Text>
          </Box>
          <Box sx={styles.buttonArea}>
            {/*開く・閉じるボタン*/}
            <Button
              onClick={handleToggle}
              colorScheme={"teal.500"}
              rightIcon={isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
              sx={{ ...styles.buttonBase, ...styles.buttonOpenClose }}
            >
              <Text sx={styles.buttonLabel}>
                {isOpen ? labelButtonLabelClose : labelButtonLabelOpen}
              </Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const CMCardEmailLogStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "mainBlock",
    "headerArea",
    "operationAndDateArea",
    "operationArea",
    "operationLabel",
    "operationSeparator",
    "operationValue",
    "date",
    "subjectParentArea",
    "subjectChildArea",
    "subjectLabel",
    "subjectSeparator",
    "subjectValue",
    "subjectUrl",
    "titleArea",
    "title",
    "mailTextArea",
    "commentBoxOpen",
    "commentBoxClose",
    "comment",
    "buttonArea",
    "buttonBase",
    "buttonOpenClose",
    "buttonLabel",
  ],
  baseStyle: {
    container: {
      // minW: "600px",
      overflow: "hidden",

      paddingLeft: "12px",
      paddingRight: "12px",
      paddingTop: "10px",
      paddingBottom: "16px",
      border: "solid 1px",
      borderColor: "gray.300",
      borderRadius: "lg",
    },

    mainBlock: { display: "flex", flexDirection: "column" },

    headerArea: {
      display: "flex",
      flexDirection: "column",
      color: "gray.500",
      borderWidth: "1px",
      borderLeftStyle: "none",
      borderRightStyle: "none",
      borderColor: "transparent",
      borderBottomColor: "gray.400",
    },

    operationAndDateArea: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      color: "gray.500",
    },
    operationArea: {
      textOverflow: "ellipsis",

      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      width: "fit-content",
      marginRight: "23px",
    },
    operationLabel: {
      fontSize: "12px",
      whiteSpace: "nowrap",
    },
    operationSeparator: {
      marginLeft: "2px",
      marginRight: "6px",
      fontSize: "12px",
    },
    operationValue: {
      wordBreak: "break-all",
    },
    date: {
      marginLeft: "auto",
      right: "0px",
    },

    subjectParentArea: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "start",
      fontSize: "14px",
      color: "gray.500",
    },
    subjectChildArea: {
      flex: "0 1 auto",
      minW: 0,

      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      width: "fit-content",
      marginRight: "10px",
    },
    subjectLabel: {
      fontSize: "12px",
      whiteSpace: "nowrap",
    },
    subjectSeparator: {
      marginLeft: "2px",
      marginRight: "6px",
      fontSize: "12px",
    },
    subjectValue: {
      wordBreak: "break-all",
    },
    subjectUrl: {
      marginRight: "4px",
      color: "blue.600",
      wordBreak: "break-all",
    },

    titleArea: {
      display: "flex",
      flexDirection: "row",
      alignItems: "start",
      mt: "7px",
      mb: "8px",
    },
    title: {
      wordBreak: "break-word",
      fontSize: "16px",
      color: "gray.900",
    },

    mailTextArea: {
      position: "relative",
      marginTop: "14px",
      marginLeft: "10px",
    },
    commentBoxOpen: {
      h: "fit-content",
      color: "gray.900",
      overflow: "hidden",
    },
    commentBoxClose: {
      color: "gray.900",
      display: "-webkit-box",
      // "-webkit-box-orient": "vertical",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      // "-webkit-line-clamp": "1",
      WebkitLineClamp: "1",
    },
    comment: {
      whiteSpace: "pre-wrap",
    },

    buttonArea: {
      display: "flex",
      flexDirection: "row",
      marginTop: "8px",
    },
    buttonBase: {
      h: "24px",
      w: "108px",
      fontSize: "12px",
      marginLeft: "auto",
      right: "0px",
      backgroundColor: "teal.500",
      color: "white",
      px: "8px",
    },
    buttonOpenClose: {},
    buttonLabel: {},
  },
};
